import { useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle, ListChecks } from 'lucide-react';
import {
  Badge,
  DataTable,
  PageContainer,
  PageHeader,
  Pagination,
  SegmentedControl,
  StatusMessage,
} from '@aimsos/ui';
import { useHitlTasks } from '../hooks';
import { TaskDecisionSlideOut } from '../components';

const STATUS_OPTIONS = ['pending', 'resolved', 'timed_out', 'all'];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

/**
 * The Work Surface task inbox: every run paused on a decision assigned to
 * the calling user — LangGraph tool approvals and Temporal workflow gates
 * alike. Clicking a row opens the decision panel; a submitted decision
 * resumes the suspended run and the row leaves the pending list.
 */
const TaskInboxPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('pending');
  const [selectedTask, setSelectedTask] = useState(null);
  const { items, meta, page, loading, error, resolving, goToPage, resolve } =
    useHitlTasks({ status });

  const totalItems = meta.totalItems ?? meta.total_items ?? items.length;
  const totalPages = meta.totalPages ?? meta.total_pages ?? 1;
  const perPage = meta.perPage ?? meta.per_page ?? 20;

  const columns = useMemo(
    () => [
      {
        key: 'summary',
        label: t('tasks.columns.decision'),
        truncate: true,
        render: (_value, row) => row.render?.summary || row.nodeId,
        getTooltipText: (_value, row) => row.render?.summary,
      },
      {
        key: 'hitlType',
        label: t('tasks.columns.source'),
        render: (value) => <Badge>{value}</Badge>,
      },
      { key: 'status', label: t('tasks.columns.status') },
      {
        key: 'createdAt',
        label: t('tasks.columns.created'),
        render: (value) => formatDate(value),
      },
      {
        key: 'expiresAt',
        label: t('tasks.columns.expires'),
        render: (value) => formatDate(value),
      },
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => STATUS_OPTIONS.map((option) => ({ value: option, label: t(`tasks.status.${option}`) })),
    [t],
  );

  return (
    <PageContainer>
      <PageHeader
        className="px-6 py-4"
        icon={<ListChecks className="h-5 w-5" />}
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
        actions={
          <SegmentedControl
            value={status}
            onChange={setStatus}
            options={statusOptions}
          />
        }
      />

      {error ? (
        <StatusMessage
          icon={AlertTriangle}
          title={t('tasks.loadFailed')}
          subtitle={error?.message || String(error)}
        />
      ) : (
        <div className="flex flex-col gap-3 px-6 pb-6">
          <DataTable
            columns={columns}
            data={items}
            loading={loading}
            emptyMessage={t('tasks.empty')}
            onRowClick={(row) => setSelectedTask(row)}
          />
          <Pagination
            totalItems={totalItems}
            currentPage={page}
            totalPages={totalPages}
            perPage={perPage}
            hasPrevious={page > 1}
            hasNext={page < totalPages}
            onPrevPage={() => goToPage(page - 1)}
            onNextPage={() => goToPage(page + 1)}
          />
        </div>
      )}

      <TaskDecisionSlideOut
        key={selectedTask?.contractId}
        task={selectedTask}
        isOpen={Boolean(selectedTask)}
        onClose={() => setSelectedTask(null)}
        onResolve={resolve}
        resolving={resolving}
      />
    </PageContainer>
  );
};

export default TaskInboxPage;
